import {ChangeEvent, FC, useRef} from 'react';
import Input from './Input';
import Cluster from './layouts/Cluster';
import Errors from './Errors';
import WithLabel from './WithLabel';
import DisplayLine from './DisplayLine';

export interface DateProps {
  name: string;
  value?: string;
  autoFocusDatePart?: 'day' | 'month' | 'year';
  onChange?: (e: ChangeEvent<HTMLInputElement> | { target: { name: string, value: string } }) => void;
  showTextErrors?: boolean;
  errors?: string[] | boolean;
  readOnly?: boolean;
  label?: string;
}

const formatDate = ({ day, month, year }) =>
  day || month || year ? `${day}-${month}-${year}` : '';

const DateInputs: FC<DateProps> = ({
  value: valueArg,
  name: dateName,
  autoFocusDatePart,
  onChange,
  showTextErrors = true,
  errors,
  readOnly,
  label,
}) => {
  const dayInput = useRef(null);
  const monthInput = useRef(null);
  const yearInput = useRef(null);

  if (readOnly) {
    return <DisplayLine label={label} value={valueArg} />;
  }

  // matches date format 'd-m-Y' (inc '' and '--')
  let match: RegExpMatchArray | null = valueArg?.match(/^(\d{0,2})?(?:-(\d{0,2}))?(?:-(\d{0,4}))?$/);
  if (valueArg === null || valueArg === undefined || !match) {
    console.error(`Invalid value format in SplitDate component:
      received ${valueArg}
      expected format string 'd-m-Y'`);
    match = ['', '', '', ''] as RegExpMatchArray;
  }

  const [, day = '', month = '', year = ''] = match;

  const onChangeHandler = (name, value) =>
    onChange({
      target: {
        value: formatDate({ day, month, year, [name]: value }),
        name: dateName,
      },
    });

  const onKeyDownHandler = (e) => {
    const { key, target } = e;
    const specialKeys = [
      'Meta',
      'Ctrl',
      'Alt',
      'Tab',
      'ArrowRight',
      'ArrowLeft',
      'Delete',
      'Enter',
    ];
    const whitelistedKeys = [...'0123456789', 'Backspace'];
    if (specialKeys.some((k) => k === key)) return;
    if (!whitelistedKeys.includes(key)) e.preventDefault();
    if (String(window.getSelection())) target.value = '';
    const { value, name, maxLength } = target;

    if (
      value.length === maxLength &&
      whitelistedKeys.includes(key) &&
      key !== 'Backspace'
    ) {
      if (name === 'day') {
        onChangeHandler('month', '');
        monthInput.current.focus();
      } else if (name === 'month') {
        onChangeHandler('year', '');
        yearInput.current.focus();
      }
    } else if (!value.length && key === 'Backspace') {
      if (name === 'year') monthInput.current.focus();
      if (name === 'month') dayInput.current.focus();
    }
  };

  const input = (
    <>
      <Cluster flex={1} hGap="s">
        <Input
          value={day}
          type="text"
          inputMode="numeric"
          name="day"
          autoFocus={autoFocusDatePart === 'day'}
          onChange={({ target: { name, value } }) =>
            onChangeHandler(name, value)
          }
          ref={dayInput}
          onKeyDown={onKeyDownHandler}
          showTextErrors={false}
          maxLength={2}
          placeholder="DD"
          errors={!!errors}
        />
        <Input
          name="month"
          type="text"
          inputMode="numeric"
          value={month}
          autoFocus={autoFocusDatePart === 'month'}
          onChange={({ target: { name, value } }) =>
            onChangeHandler(name, value)
          }
          ref={monthInput}
          onKeyDown={onKeyDownHandler}
          maxLength={2}
          placeholder="MM"
          errors={!!errors}
        />
        <Input
          type="text"
          inputMode="numeric"
          name="year"
          value={year}
          autoFocus={autoFocusDatePart === 'year'}
          onChange={({ target: { name, value } }) =>
            onChangeHandler(name, value)
          }
          ref={yearInput}
          onKeyDown={onKeyDownHandler}
          maxLength={4}
          placeholder="JJJJ"
          errors={!!errors}
        />
      </Cluster>
      {showTextErrors ? <Errors {...{ errors }} /> : null}
    </>
  );

  return label ? <WithLabel label={label}>{input}</WithLabel> : input;
};

export default DateInputs;
